import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { Search, ShieldCheck } from "lucide-react";
import { api, fileUrl } from "@/lib/api";
import { LogoMark } from "@/components/LogoMark";
import { StoreFooter } from "@/components/StoreFooter";

function StorefrontHeader() {
  return (
    <>
      <div className="gov-strip">
        <span className="tri"><span className="b saffron" /><span className="b white" /><span className="b green" /></span>
        <Link to="/about">About us</Link>
        <Link to="/authorised-users">Authorised users</Link>
        <Link to="/contact">Contact</Link>
      </div>
      <header className="store-header">
        <Link to="/" className="store-brand"><LogoMark small /><span>GI Bharat<small>By True Vision Welfare Society</small></span></Link>
        <nav><Link to="/login" className="store-login">Sign in</Link></nav>
      </header>
    </>
  );
}

export default function AuthorisedUsers() {
  const [items, setItems] = useState([]);
  const [counters, setCounters] = useState({ authorised_users_total: 500, crafts: [] });
  const [filter, setFilter] = useState("");
  const [craft, setCraft] = useState("all");

  useEffect(() => {
    api.get("/authorised-users?limit=5000").then(r => setItems(r.data)).catch(() => {});
    api.get("/counters").then(r => setCounters(r.data)).catch(() => {});
  }, []);

  const crafts = useMemo(() => Array.from(new Set(items.map(x => x.craft_name).filter(Boolean))), [items]);
  const filtered = useMemo(() => {
    const q = filter.trim().toLowerCase();
    return items.filter(x =>
      (craft === "all" || x.craft_name === craft) &&
      (!q || [x.firm_name, x.proprietor_name, x.item_name, x.craft_name].filter(Boolean).some(v => v.toLowerCase().includes(q)))
    );
  }, [items, filter, craft]);

  return (
    <main className="storefront" data-testid="au-page">
      <StorefrontHeader />

      <section className="section">
        <p className="eyebrow">DIRECTORY</p>
        <h1 style={{ color: "var(--tv-green)" }}>Registered authorised users</h1>
        <p className="subtitle">A public directory of GI-authorised makers and firms registered with True Vision Welfare Society. Search by name, firm, or craft.</p>
      </section>

      <section className="stats-band" data-testid="au-stats">
        <div className="inner">
          <div><div className="n" data-testid="au-total">{counters.authorised_users_total}+</div><div className="lbl">Authorised users</div></div>
          {(counters.crafts || []).map((c, i) => (
            <div key={i}>
              <div className="n">{c.count}+</div>
              <div className="lbl">{c.name}</div>
              <div className="desc">{c.description}</div>
            </div>
          ))}
        </div>
      </section>

      <section className="section">
        <div className="toolbar">
          <div style={{ display: "flex", gap: 10, alignItems: "center" }}>
            <div className="store-search" style={{ maxWidth: 420 }}>
              <input placeholder="Search by name, firm, item…" value={filter} onChange={e => setFilter(e.target.value)} data-testid="au-search" />
              <button type="button" aria-label="Search"><Search size={16} /></button>
            </div>
            <select value={craft} onChange={e => setCraft(e.target.value)} data-testid="au-craft-filter" style={{ maxWidth: 240 }}>
              <option value="all">All crafts</option>
              {crafts.map(c => <option key={c} value={c}>{c}</option>)}
            </select>
          </div>
          <span className="muted">{filtered.length} match{filtered.length === 1 ? "" : "es"}</span>
        </div>

        {filtered.length ? (
          <div className="store-grid" data-testid="au-grid">
            {filtered.map(u => (
              <div className="product-card" key={u.id} data-testid={`au-${u.id}`}>
                <div className="product-art">
                  {u.photo ? <img src={fileUrl(u.photo)} alt="" /> : <span className="placeholder">✦</span>}
                </div>
                <div className="product-body">
                  <span className="verified"><ShieldCheck size={12} /> GI authorised</span>
                  <h4>{u.firm_name || u.proprietor_name}</h4>
                  {u.firm_name && <p style={{ margin: 0 }}>{u.proprietor_name}</p>}
                  <p style={{ color: "var(--saffron-dark)", fontWeight: 700 }}>{u.craft_name || u.item_name}</p>
                  {u.user_registration_no && <p style={{ margin: 0, fontSize: 12 }} data-testid={`au-${u.id}-reg`}><b>Reg. No.:</b> {u.user_registration_no}</p>}
                  <p style={{ margin: 0 }}>{u.address}</p>
                  {(u.mobile || u.email) && <p className="muted" style={{ margin: 0, fontSize: 12 }}>{u.mobile} {u.mobile && u.email ? "·" : ""} {u.email}</p>}
                </div>
              </div>
            ))}
          </div>
        ) : (
          <div className="empty-state" data-testid="au-empty">
            <ShieldCheck size={26} />
            <h3>No records match your search</h3>
            <p>Try clearing the search or a different craft filter.</p>
          </div>
        )}
      </section>

      <StoreFooter />
    </main>
  );
}
